import Head from "next/head";
import { Layout } from "@features/Layout";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import styled from "styled-components";
import { Container } from "@shared/ui/sharedstyles";
import { Tabs } from "@shared/ui/Tabs";
import React, { useMemo, useState } from "react";
import { ContactInfo } from "@entities/Contacts/ContactInfo";
import { Typography } from "@shared/ui/Typography";
import dynamic from "next/dynamic";
import { useTranslation } from "next-i18next";
import { axiosInstance } from "@shared/api";
import { IContactsResponse } from "@shared/types";
import { useRouter } from "next/router";

const YandexMap = dynamic(() => import("@features/YandexMap"), {
  ssr: true,
});

interface IProps {
  contacts: IContactsResponse;
}

export default function Index(props: IProps) {
  const { t } = useTranslation();
  const { locale, push, pathname, query } = useRouter();
  const [activeCityId, setActiveCityId] = useState(() => {
    const hasQueryCity = props.contacts.cities.some(
      (city) => city.id === Number(query?.cityId)
    );
    return hasQueryCity
      ? Number(query?.cityId)
      : props.contacts.cities?.[0]?.id;
  });

  const [currentAddressId, setCurrentAddressId] = useState(null);

  const currentCity = useMemo(() => {
    const city = props.contacts.cities.find((item) => item.id === activeCityId);
    setCurrentAddressId(city?.addresses?.[0]?.id ?? 0);
    return city;
  }, [activeCityId, locale]);

  const coordinates = useMemo(
    () => props.contacts.cities.map((value) => value.addresses).flat(1),
    [locale]
  );

  const currentCenter = useMemo(
    () =>
      coordinates.find((item) => item.id === currentAddressId) ??
      currentCity?.addresses[0],
    [currentAddressId, locale]
  );

  const setCity = (id: number) => {
    push(
      {
        href: pathname,
        query: { cityId: id },
      },
      {
        href: pathname,
        query: { cityId: id },
      },
      { shallow: true }
    );
    setActiveCityId(id);
  };

  return (
    <>
      <Head>
        <title>{t(`БНК Финанс Казахстан`)}</title>
      </Head>
      <StyledContent>
        <StyledTabs
          tabs={props.contacts.cities.map((value) => ({
            id: value.id,
            title: value.name,
          }))}
          activeTab={activeCityId as number}
          setActiveTab={setCity}
        />
        <StyledContact>
          <StyledMapWrapper>
            <YandexMap
              center={[
                Number(currentCenter.latitude),
                Number(currentCenter.longitude),
              ]}
              placemarks={coordinates.map((item) => [
                Number(item.latitude),
                Number(item.longitude),
              ])}
            />
          </StyledMapWrapper>

          <StyledContactInfoWrapper>
            <ContactInfo title={t(`Адрес офиса`)} iconName="Location">
              <StyledAddressesWrapper>
                {currentCity?.addresses.map((address) => (
                  <StyledAddress
                    onClick={() => setCurrentAddressId(address.id)}
                    tag="p"
                    weight={400}
                    key={address.id}
                  >
                    {address.name}
                  </StyledAddress>
                ))}
              </StyledAddressesWrapper>
            </ContactInfo>
            <ContactInfo title={t(`Часы работы`)} iconName="Time">
              <Typography tag="p" weight={400}>
                {currentCity.addresses?.[0].working_time}
              </Typography>
            </ContactInfo>
            {/*            <ContactInfo
              title={t(`Телефон`)}
              subtitle={tabs[activeTab].phone}
              iconName="Call"
            /> */}
            <StyledCallCenterWrapper>
              <a
                href={`tel:${props.contacts.phone}`}
                style={{ whiteSpace: "nowrap", display: "block" }}
              >
                <StyledCallCenterText>
                  {t(`Единый call-центр`)} <b>{props.contacts.phone}</b>
                </StyledCallCenterText>
              </a>
            </StyledCallCenterWrapper>
          </StyledContactInfoWrapper>
        </StyledContact>
      </StyledContent>
    </>
  );
}

Index.getLayout = function getLayout(page) {
  return <Layout>{page}</Layout>;
};

export async function getServerSideProps(ctx) {
  const response = await axiosInstance("/contacts", {
    headers: { "Accept-Language": ctx.locale },
  });

  return {
    props: {
      ...(await serverSideTranslations(ctx.locale, ["common"])),
      contacts: response.data,
    },
  };
}

const StyledContent = styled(Container)`
  padding-top: 8rem;
  padding-bottom: 8rem;

  @media screen and (max-width: ${(props) => props.theme.sizes.tabletMin}) {
    padding-top: 4.8rem;
    padding-bottom: 6.4rem;
  }
`;

const StyledTabs = styled(Tabs)`
  margin-bottom: 4.8rem;

  @media screen and (max-width: ${(props) => props.theme.sizes.tabletMin}) {
    margin-bottom: 4rem;
  }
`;

const StyledMapWrapper = styled.div`
  border-radius: 0.4rem;
  overflow: hidden;
  height: 32.5rem;

  @media screen and (max-width: ${(props) => props.theme.sizes.tabletMin}) {
    order: 2;
    height: 47.4rem;
  }
`;

const StyledContact = styled.div`
  margin-bottom: 4.8rem;
  display: grid;
  grid-template-columns: 1fr 0.5fr;
  grid-gap: 3.2rem;

  @media screen and (max-width: ${(props) => props.theme.sizes.tabletMin}) {
    grid-template-columns: 1fr;
  }
`;

const StyledContactInfoWrapper = styled.div`
  display: grid;
  grid-gap: 4rem;
  align-content: flex-start;

  @media screen and (max-width: ${(props) => props.theme.sizes.tabletMin}) {
    grid-gap: 3.2rem;
    order: 1;
  }
`;

const StyledCallCenterWrapper = styled.div`
  border-radius: 0.8rem;
  padding: 2.4rem;
  background-color: ${(props) => props.theme.colors.primary_light_gray}1a;
`;

const StyledAddressesWrapper = styled.div`
  display: grid;
  grid-gap: 0.4rem;
`;

const StyledAddress = styled(Typography)`
  &:hover {
    cursor: pointer;
  }
`;

const StyledCallCenterText = styled(Typography)`
  white-space: break-spaces;
`;
